import React, { useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
    ChevronLeft,
    CheckCircle2,
    Wallet,
    ShieldCheck,
    Calendar,
    MapPin,
    Banknote,
    Car,
    Users,
    Bus,
} from "lucide-react";
import { useData } from "../context/DataContext";
import { useAuth } from "../context/AuthContext";
import { formatCurrency } from "../lib/format";
import PhoneInputField from "../components/PhoneInputField";

const Steps = ["Trip dates", "Traveller info", "Confirm & Pay"];

const VEHICLE_LABELS = {
    sedan: { label: "Sedan", sub: "4 + driver", icon: Car },
    suv: { label: "SUV", sub: "6 + driver · Innova Crysta", icon: Users },
    tempo: { label: "Tempo Van", sub: "12 + driver", icon: Bus },
};

const resolvePrices = (pkg) => {
    if (!pkg) return { sedan: 0, suv: 0, tempo: 0 };
    const sedan = pkg.sedanPrice ?? pkg.price ?? 0;
    const suv = pkg.suvPrice ?? Math.round((sedan * 1.30) / 100) * 100;
    const tempo = pkg.tempoPrice ?? Math.round((sedan * 1.75) / 100) * 100;
    return { sedan, suv, tempo };
};

const PackageBooking = () => {
    const { pkgId } = useParams();
    const navigate = useNavigate();
    const [search] = useSearchParams();
    const { packages, createBooking, settings } = useData();
    const { user } = useAuth();

    const pkg = packages.find((p) => p.id === pkgId);
    const initialVehicle = ["sedan", "suv", "tempo"].includes(search.get("vehicle"))
        ? search.get("vehicle")
        : "sedan";

    const [step, setStep] = useState(0);
    const [vehicleClass, setVehicleClass] = useState(initialVehicle);
    const [startDate, setStartDate] = useState("");
    const [adults, setAdults] = useState(2);
    const [children, setChildren] = useState(0);

    const [name, setName] = useState(user?.name || "");
    const [email, setEmail] = useState(user?.email || "");
    const [phone, setPhone] = useState(user?.phone || "");
    const [notes, setNotes] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const prices = useMemo(() => resolvePrices(pkg), [pkg]);
    const totalAmount = useMemo(() => prices[vehicleClass] || 0, [prices, vehicleClass]);

    if (!pkg) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center">
                <div className="text-center">
                    <p className="text-ink-light">Package not found.</p>
                    <button onClick={() => navigate("/tour-packages")} className="mt-4 text-blue-500 font-semibold">Back to packages</button>
                </div>
            </div>
        );
    }

    if (settings && settings.bookingsEnabled === false) {
        return (
            <div className="min-h-[60vh] max-w-md mx-auto px-4 py-20 text-center">
                <h1 className="font-display text-3xl text-ink">Bookings paused</h1>
                <p className="text-ink-light mt-2">Online bookings are temporarily disabled.</p>
            </div>
        );
    }

    const next = () => setStep((s) => Math.min(2, s + 1));
    const prev = () => setStep((s) => Math.max(0, s - 1));

    const submit0 = (e) => {
        e.preventDefault();
        if (!startDate) return toast.error("Please choose a start date.");
        next();
    };
    const submit1 = (e) => {
        e.preventDefault();
        if (!name || !email || !phone) return toast.error("Please complete all details.");
        if (!/\S+@\S+\.\S+/.test(email)) return toast.error("Enter a valid email.");
        if (phone.replace(/\D/g, "").length < 10) return toast.error("Enter a valid phone number.");
        next();
    };
    const handleConfirm = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const created = await createBooking({
                type: "package",
                packageId: pkg.id,
                startDate,
                adults,
                children,
                vehicleClass,
                customer: { name, email, phone, notes },
            });
            toast.success("Booking confirmed! Confirmation email sent.");
            navigate(`/booking/confirmation/${created.id}`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not create booking.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="bg-cream-100 min-h-screen pb-20" data-testid="package-booking-page">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
                <button onClick={() => navigate(-1)} className="text-sm text-ink-light hover:text-ink flex items-center gap-1 mb-4">
                    <ChevronLeft className="w-4 h-4" /> Back
                </button>

                <div className="flex items-center justify-center gap-2 md:gap-4 mb-8">
                    {Steps.map((s, i) => (
                        <React.Fragment key={s}>
                            <div className={`flex items-center gap-2 ${i === step ? "text-blue-500" : i < step ? "text-blue-600" : "text-ink-light/60"}`}>
                                <span className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                                    i === step ? "bg-blue-500 text-white" : i < step ? "bg-blue-600 text-white" : "bg-white border border-cream-200"
                                }`}>
                                    {i < step ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
                                </span>
                                <span className="hidden md:inline text-sm font-semibold">{s}</span>
                            </div>
                            {i < Steps.length - 1 && (
                                <span className={`w-8 md:w-16 h-0.5 ${i < step ? "bg-blue-600" : "bg-cream-200"}`} />
                            )}
                        </React.Fragment>
                    ))}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                    <div className="lg:col-span-8 space-y-6">
                        {step === 0 && (
                            <form onSubmit={submit0} className="bg-white rounded-2xl border border-cream-200 p-6">
                                <h2 className="font-display text-2xl text-ink font-bold mb-4">Choose travel dates</h2>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="md:col-span-1">
                                        <label className="text-xs font-semibold uppercase tracking-wider text-ink-light">Start date</label>
                                        <input type="date" min={new Date().toISOString().slice(0, 10)} value={startDate} onChange={(e) => setStartDate(e.target.value)} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-cream-200 bg-white" data-testid="pb-start-date" required />
                                    </div>
                                    <div>
                                        <label className="text-xs font-semibold uppercase tracking-wider text-ink-light">Adults</label>
                                        <input type="number" min={1} max={20} value={adults} onChange={(e) => setAdults(Number(e.target.value))} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-cream-200 bg-white" data-testid="pb-adults" />
                                    </div>
                                    <div>
                                        <label className="text-xs font-semibold uppercase tracking-wider text-ink-light">Children</label>
                                        <input type="number" min={0} max={10} value={children} onChange={(e) => setChildren(Number(e.target.value))} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-cream-200 bg-white" data-testid="pb-children" />
                                    </div>
                                </div>

                                <div className="mt-6">
                                    <label className="text-xs font-semibold uppercase tracking-wider text-ink-light">Vehicle</label>
                                    <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="pb-vehicle-picker">
                                        {Object.entries(VEHICLE_LABELS).map(([id, v]) => {
                                            const Icon = v.icon;
                                            const active = vehicleClass === id;
                                            return (
                                                <button
                                                    key={id}
                                                    type="button"
                                                    onClick={() => setVehicleClass(id)}
                                                    data-testid={`pb-vehicle-${id}`}
                                                    className={`text-left rounded-xl border p-3 flex items-center gap-3 transition ${
                                                        active
                                                            ? "border-blue-500 bg-blue-50"
                                                            : "border-cream-200 hover:border-blue-300"
                                                    }`}
                                                >
                                                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${active ? "bg-blue-500 text-white" : "bg-cream-100 text-ink"}`}>
                                                        <Icon className="w-4 h-4" />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="text-sm font-semibold text-ink leading-tight">{v.label}</div>
                                                        <div className="text-[11px] text-ink-light">{v.sub}</div>
                                                    </div>
                                                    <div className="text-sm font-display font-semibold text-blue-500">
                                                        {formatCurrency(prices[id])}
                                                    </div>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>

                                <div className="flex justify-end mt-5">
                                    <button type="submit" className="px-7 py-3 rounded-xl bg-blue-500 hover:bg-blue-600 text-white font-semibold transition shadow-sm" data-testid="pb-step0-next">Continue</button>
                                </div>
                            </form>
                        )}

                        {step === 1 && (
                            <form onSubmit={submit1} className="bg-white rounded-2xl border border-cream-200 p-6">
                                <h2 className="font-display text-2xl text-ink font-bold mb-4">Lead traveller info</h2>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-xs font-semibold uppercase tracking-wider text-ink-light">Full name</label>
                                        <input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-cream-200 bg-white" data-testid="pb-name" required />
                                    </div>
                                    <div>
                                        <label className="text-xs font-semibold uppercase tracking-wider text-ink-light">Email</label>
                                        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-cream-200 bg-white" data-testid="pb-email" required />
                                    </div>
                                    <div className="md:col-span-2">
                                        <label className="text-xs font-semibold uppercase tracking-wider text-ink-light">Phone</label>
                                        <PhoneInputField value={phone} onChange={setPhone} testId="pb-phone" required />
                                    </div>
                                    <div className="md:col-span-2">
                                        <label className="text-xs font-semibold uppercase tracking-wider text-ink-light">Special notes</label>
                                        <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-cream-200 bg-white" data-testid="pb-notes" />
                                    </div>
                                </div>
                                <div className="flex justify-between mt-5">
                                    <button type="button" onClick={prev} className="px-5 py-3 rounded-xl border border-cream-200 text-ink font-semibold">Back</button>
                                    <button type="submit" className="px-7 py-3 rounded-xl bg-blue-500 hover:bg-blue-600 text-white font-semibold transition shadow-sm" data-testid="pb-step1-next">Continue</button>
                                </div>
                            </form>
                        )}

                        {step === 2 && (
                            <form onSubmit={handleConfirm} className="bg-white rounded-2xl border border-cream-200 p-6">
                                <h2 className="font-display text-2xl text-ink font-bold mb-2">Confirm your package</h2>
                                <p className="text-sm text-ink-light mb-4">No prepayment required — pay the driver on arrival.</p>

                                <div className="rounded-xl border-2 border-blue-500 bg-blue-50 p-5 flex items-center gap-4" data-testid="pb-pay-direct-driver">
                                    <span className="w-12 h-12 rounded-xl bg-blue-500 text-white flex items-center justify-center shrink-0">
                                        <Wallet className="w-6 h-6" />
                                    </span>
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2">
                                            <span className="font-semibold text-ink">Direct to Driver</span>
                                            <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-blue-500 text-white font-semibold">On arrival</span>
                                        </div>
                                        <div className="text-sm text-ink-light mt-1">Cash or UPI directly to your driver. Receipt on demand.</div>
                                    </div>
                                </div>

                                <div className="flex items-center gap-2 mt-4 px-4 py-3 rounded-lg bg-cream-100 text-ink-light text-xs border border-cream-200">
                                    <ShieldCheck className="w-4 h-4 text-blue-500 shrink-0" />
                                    <span>Your booking is held without prepayment. We'll send a confirmation email and our team will reconfirm on WhatsApp before pickup.</span>
                                </div>

                                <div className="flex justify-between mt-5">
                                    <button type="button" onClick={prev} className="px-5 py-3 rounded-xl border border-cream-200 text-ink font-semibold">Back</button>
                                    <button type="submit" disabled={submitting} className="px-8 py-3 rounded-xl bg-blue-500 hover:bg-blue-600 text-white font-semibold transition shadow-md brand-shadow disabled:opacity-60 flex items-center gap-2" data-testid="pb-confirm-pay">
                                        <Banknote className="w-4 h-4" />
                                        {submitting ? "Booking…" : `Confirm booking · ${formatCurrency(totalAmount)}`}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>

                    <aside className="lg:col-span-4">
                        <div className="bg-white rounded-2xl border border-cream-200 overflow-hidden sticky top-24">
                            <img src={pkg.image} alt={pkg.route} className="w-full h-40 object-cover" />
                            <div className="p-5">
                                <div className="text-xs font-semibold uppercase tracking-wider text-blue-500">From {pkg.startingCity}</div>
                                <h3 className="font-display text-xl text-ink font-bold leading-tight">{pkg.route}</h3>
                                <div className="text-xs text-ink-light flex items-center gap-3 mt-1">
                                    <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {pkg.days}D / {pkg.nights}N</span>
                                    <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {pkg.km} km</span>
                                </div>
                                <dl className="mt-4 space-y-2 text-sm pt-4 border-t border-cream-200">
                                    <div className="flex justify-between"><dt className="text-ink-light">Vehicle</dt><dd className="font-medium text-ink">{VEHICLE_LABELS[vehicleClass].label}</dd></div>
                                    <div className="flex justify-between"><dt className="text-ink-light">Travellers</dt><dd className="font-medium text-ink">{adults} adults · {children} kids</dd></div>
                                    <div className="flex justify-between"><dt className="text-ink-light">Start date</dt><dd className="font-medium text-ink">{startDate || "—"}</dd></div>
                                </dl>
                                <div className="mt-4 pt-4 border-t border-cream-200 flex items-center justify-between">
                                    <span className="text-ink-light text-sm">Total</span>
                                    <span className="font-display text-2xl font-bold text-blue-500" data-testid="pb-total">{formatCurrency(totalAmount)}</span>
                                </div>
                                <div className="text-[11px] text-ink-light mt-1 text-right">Payable to driver on arrival</div>
                            </div>
                        </div>
                    </aside>
                </div>
            </div>
        </div>
    );
};

export default PackageBooking;
